import {
    Box,
    Button,
    FormControl,
    Paper,
    Stack,
    TableCell,
    Typography,
} from "@mui/material";

import styled, { keyframes } from "styled-components";

export const Cell = styled(TableCell)`
    background-color: #fafafa;
`;

export const PdfDocList = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
`;

export const PdfDocListItem = styled(Button)`
    && {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        width: 100%;
        padding: 14px 16px;
        border: 1px solid rgba(0, 0, 0, 0.08);
        border-radius: 12px;
        text-transform: none;
        color: inherit;
        background: #ffffff;
        font-family: Nunito, sans-serif;

        &:hover {
            background-color: rgba(237, 108, 2, 0.04);
            border-color: rgba(237, 108, 2, 0.35);
        }
    }
`;

export const PdfDocListItemName = styled(Typography)`
    && {
        font-family: Nunito, sans-serif;
        font-size: 15px;
        font-weight: 700;
        color: #ed6c02;
        line-height: 1.3;
        text-align: left;
    }
`;

export const PdfDocListItemIcon = styled(Box)`
    display: flex;
    align-items: center;
    color: #ed6c02;
    flex-shrink: 0;
`;

/* ------------------------------------------------------------------------ *
 * Reports design system
 *
 * Same card / chart language as `LeadGeneralReportsPage.styled` so the
 * tracker reports read as part of the same reporting surface.
 * ------------------------------------------------------------------------ */

const ORANGE = "#ED6C02";

export const PageWrapper = styled(Box)`
    display: inline-block;
    width: 100%;
    color: #0f172a;
    font-family: "Nunito", sans-serif;
`;

export const HeaderRow = styled(Stack)`
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
    flex-wrap: wrap;
    margin-bottom: 24px;
`;

export const PageTitle = styled(Box)`
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: #0f172a;
    line-height: 1.15;
    @media (max-width: 600px) {
        font-size: 22px;
    }
`;

export const PageSubtitle = styled(Box)`
    font-size: 13px;
    color: #64748b;
    margin-top: 4px;
    max-width: 480px;
    @media (max-width: 600px) {
        font-size: 12px;
    }
`;

export const MetricsRow = styled(Box)`
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(2, minmax(0, 1fr));

    @media (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

export const MetricCard = styled(Paper)`
    && {
        padding: 18px 24px;
        border-radius: 10px;
        border: 1px solid rgba(0, 0, 0, 0.05);
        box-shadow: none;
        background: #ffffff;
        display: flex;
        flex-direction: column;
        gap: 10px;
        height: 100%;
        font-family: "Nunito", sans-serif;
        transition: transform 0.2s ease;
        &:hover {
            transform: translateY(-2px);
        }
        @media (max-width: 600px) {
            padding: 14px 16px;
            gap: 8px;
        }
    }
`;

export const MetricLabel = styled(Box)`
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.05em;
    color: rgba(0, 0, 0, 0.6);
    text-transform: uppercase;
    font-family: "Nunito", sans-serif;
`;

export const MetricValue = styled(Box)`
    font-size: clamp(18px, 2.4vw, 32px);
    font-weight: 700;
    color: #0f172a;
    line-height: 1.15;
    font-family: "Nunito", sans-serif;
    @media (max-width: 600px) {
        font-size: 26px;
    }
`;

export const MetricHint = styled(Box)`
    font-size: 12px;
    color: #64748b;
    font-family: "Nunito", sans-serif;
`;

export const IconBadge = styled(Box)<{ $bg: string; $color?: string }>`
    border-radius: 8px;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(p) => p.$bg};
    color: ${(p) => p.$color ?? "#0F172A"};
    flex-shrink: 0;
`;

export const ProgressTrack = styled(Box)`
    width: 100%;
    height: 6px;
    border-radius: 999px;
    background: #f1f5f9;
    overflow: hidden;
`;

export const ProgressFill = styled(Box)<{ $width: number; $color: string }>`
    width: ${(p) => Math.max(0, Math.min(100, p.$width))}%;
    height: 100%;
    background: ${(p) => p.$color};
    border-radius: 999px;
    transition: width 0.4s ease;
`;

export const FilterCard = styled(Paper)`
    && {
        background: #ffffff;
        border: 1px solid rgba(0, 0, 0, 0.05);
        border-radius: 10px;
        box-shadow: none;
        padding: 22px 24px;
        overflow: visible;
        @media (max-width: 900px) {
            padding: 16px 18px;
        }
        @media (max-width: 600px) {
            padding: 12px 14px;
        }
    }
`;

/**
 * Named areas rather than plain column counts: the OR separator has to stay
 * between the date pair and the date-range select at every width, which a
 * `repeat(2, 1fr)` fallback breaks by handing it an arbitrary cell.
 */
export const FilterRow = styled(Box)`
    display: grid;
    grid-template-columns:
        minmax(150px, 1fr)
        minmax(150px, 1fr)
        44px
        minmax(170px, 1fr)
        minmax(170px, 1fr);
    grid-template-areas: "start end or range pixel";
    align-items: end;
    gap: 16px;
    width: 100%;

    /* Tablet: dates on one row, range and pixel on the next, OR spanning
       between them as a horizontal rule. */
    @media (max-width: 1200px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        grid-template-areas:
            "start end"
            "or or"
            "range pixel";
        gap: 12px 16px;
    }

    @media (max-width: 600px) {
        grid-template-columns: minmax(0, 1fr);
        grid-template-areas:
            "start"
            "end"
            "or"
            "range"
            "pixel";
        gap: 12px;
    }
`;

export const FilterField = styled(Box)<{ $area?: string }>`
    grid-area: ${(p) => p.$area ?? "auto"};
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 6px;
    position: relative;
`;

export const FilterLabel = styled(Box)`
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    letter-spacing: 0.01em;
`;

/** Keeps the react-datepicker input on the same visual rail as the MUI selects. */
export const DateInput = styled(Box)`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #ffffff;
    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;
    &:focus-within {
        border-color: ${ORANGE};
        box-shadow: 0 0 0 3px rgba(237, 108, 2, 0.12);
    }
    .react-datepicker-wrapper {
        flex: 1;
        min-width: 0;
    }
    input {
        width: 100%;
        border: none;
        outline: none;
        font-family: "Nunito", sans-serif;
        font-size: 14px;
        color: #0f172a;
        background: transparent;
        &::placeholder {
            color: #94a3b8;
        }
    }
    .MuiSvgIcon-root {
        font-size: 18px;
        color: #94a3b8;
        flex-shrink: 0;
    }
`;

export const OrDivider = styled(Box)`
    grid-area: or;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #94a3b8;

    /* Once it spans a full row it reads as a separator, not a column label. */
    @media (max-width: 1200px) {
        height: auto;
        gap: 12px;
        &::before,
        &::after {
            content: "";
            flex: 1;
            height: 1px;
            background: #e2e8f0;
        }
    }
`;

export const StyledFormControl = styled(FormControl)`
    && {
        width: 100%;
        .MuiOutlinedInput-root {
            height: 40px;
            border-radius: 8px;
            font-family: "Nunito", sans-serif;
            font-size: 14px;
            background: #ffffff;
            fieldset {
                border-color: #e2e8f0;
            }
            &:hover fieldset {
                border-color: #cbd5e1;
            }
            &.Mui-focused fieldset {
                border-color: ${ORANGE};
                border-width: 1px;
            }
        }
    }
`;

/**
 * `$flush` drops the card chrome for sections that already carry their own
 * frame (the events and event-error tables), leaving just the header and the
 * table on the page background.
 */
export const SectionCard = styled(Paper)<{ $flush?: boolean }>`
    && {
        padding: ${(p) => (p.$flush ? "0" : "22px 24px")};
        border-radius: ${(p) => (p.$flush ? "0" : "10px")};
        border: ${(p) => (p.$flush ? "none" : "1px solid rgba(0, 0, 0, 0.05)")};
        box-shadow: none;
        background: ${(p) => (p.$flush ? "transparent" : "#ffffff")};
        @media (max-width: 900px) {
            padding: ${(p) => (p.$flush ? "0" : "18px")};
        }
        @media (max-width: 600px) {
            padding: ${(p) => (p.$flush ? "0" : "14px")};
        }
    }
`;

export const SectionHeader = styled(Stack)`
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 16px;
`;

export const SectionTitle = styled(Box)`
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
`;

export const SectionSubtitle = styled(Box)`
    font-size: 13px;
    color: #64748b;
    margin-top: 2px;
`;

export const StatusChip = styled(Box)<{ $bg: string; $color: string }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: ${(p) => p.$bg};
    color: ${(p) => p.$color};
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    white-space: nowrap;
`;

export const TableWrap = styled(Box)`
    overflow-x: auto;
    border: 1px solid #eef2f6;
    border-radius: 8px;
`;

export const ReportTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
    min-width: 720px;
    font-family: "Nunito", sans-serif;
    th {
        text-align: left;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #64748b;
        padding: 12px 16px;
        background: #f8fafc;
        border-bottom: 1px solid #eef2f6;
        white-space: nowrap;
    }
    td {
        padding: 0 16px;
        height: 56px;
        border-bottom: 1px solid #f1f5f9;
        color: #0f172a;
    }
    tbody tr.report-row:hover {
        background: #f9fafb;
        cursor: pointer;
    }
    tbody tr.report-row.is-open {
        background: #fffaf5;
    }
    tbody tr.report-detail td {
        height: auto;
        padding: 0 16px 16px;
        background: #fcfcfd;
    }
`;

export const SortableHeader = styled(Box)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    user-select: none;
    &:hover {
        color: ${ORANGE};
    }
`;

export const ExpandToggle = styled(Box)<{ $open: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    color: #64748b;
    background: ${(p) => (p.$open ? "rgba(237, 108, 2, 0.1)" : "transparent")};
    transform: rotate(${(p) => (p.$open ? 180 : 0)}deg);
    transition:
        transform 0.2s ease,
        background 0.2s ease;
`;

export const EventName = styled(Box)`
    font-weight: 700;
    color: #0f172a;
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const NumericCell = styled(Box)<{ $danger?: boolean }>`
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${(p) => (p.$danger ? "#DC2626" : "#0F172A")};
`;

const shimmer = keyframes`
    0% { background-position: -468px 0; }
    100% { background-position: 468px 0; }
`;

export const SkeletonBlock = styled(Box)<{ $h?: number }>`
    height: ${(p) => p.$h ?? 20}px;
    border-radius: 8px;
    background: linear-gradient(to right, #f1f5f9 8%, #e2e8f0 18%, #f1f5f9 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.4s linear infinite forwards;
`;
